/**
 * CRM Fields API
 *
 * Handles all CRM field metadata cache API calls.
 * Used by QueryBuilder to show dynamic CRM fields from Bitrix24/etc.
 *
 * Backend caches CRM field metadata to avoid hitting external APIs on every request.
 * This API provides:
 * - Get cached fields for QueryBuilder dropdowns
 * - Refresh fields from CRM API (admin action)
 * - Get entity data by ID (for displaying current values)
 * - List supported entity types and providers
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type {
  CrmEntityType,
  GetCrmFieldsParams,
  GetCrmFieldsResponse,
  RefreshCrmFieldsRequest,
  RefreshCrmFieldsResponse,
  GetCrmEntityResponse,
  GetCrmEntityTypesResponse,
  GetCrmProvidersResponse,
  GetDepartmentsResponse,
  RefreshDepartmentsResponse,
} from '../types';

export class CrmFieldsApi extends BaseApiClient {
  /**
   * Get cached CRM fields for QueryBuilder
   * Returns fields grouped by entity type for dropdowns
   *
   * @param params.entityType - Optional filter by entity type (deal, lead, contact, company)
   */
  async getCrmFields(params?: GetCrmFieldsParams): Promise<GetCrmFieldsResponse> {
    const query = params?.entityType
      ? this.buildQuery({ entityType: params.entityType })
      : '';
    return this.request<GetCrmFieldsResponse>(`${API_ENDPOINTS.CRM_FIELDS}${query}`);
  }

  /**
   * Refresh CRM fields from external CRM API
   * This is an admin action that updates the cache
   *
   * @param entityType - Entity type to refresh (deal, lead, contact, company)
   */
  async refreshCrmFields(entityType: CrmEntityType): Promise<RefreshCrmFieldsResponse> {
    const data: RefreshCrmFieldsRequest = { entityType };
    return this.request<RefreshCrmFieldsResponse>(API_ENDPOINTS.CRM_FIELDS_REFRESH, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get CRM entity data by type and ID
   * Used to display current field values when editing rules
   *
   * @param entityType - Entity type (deal, lead, contact, company)
   * @param entityId - Entity ID in CRM system
   */
  async getCrmEntity(
    entityType: CrmEntityType,
    entityId: string
  ): Promise<GetCrmEntityResponse> {
    return this.request<GetCrmEntityResponse>(
      API_ENDPOINTS.CRM_FIELDS_ENTITY(entityType, entityId)
    );
  }

  /**
   * Get list of supported CRM entity types
   * Returns entity types available for routing rules
   */
  async getEntityTypes(): Promise<GetCrmEntityTypesResponse> {
    return this.request<GetCrmEntityTypesResponse>(API_ENDPOINTS.CRM_FIELDS_ENTITY_TYPES);
  }

  /**
   * Get list of supported CRM providers
   * Returns providers with support status (Bitrix24, AmoCRM, etc.)
   */
  async getProviders(): Promise<GetCrmProvidersResponse> {
    return this.request<GetCrmProvidersResponse>(API_ENDPOINTS.CRM_FIELDS_PROVIDERS);
  }

  /**
   * Get cached departments for routing rule dropdowns
   */
  async getDepartments(): Promise<GetDepartmentsResponse> {
    return this.request<GetDepartmentsResponse>(API_ENDPOINTS.CRM_FIELDS_DEPARTMENTS);
  }

  /**
   * Refresh departments from CRM
   */
  async refreshDepartments(): Promise<RefreshDepartmentsResponse> {
    return this.request<RefreshDepartmentsResponse>(
      API_ENDPOINTS.CRM_FIELDS_DEPARTMENTS_REFRESH,
      { method: 'POST' }
    );
  }
}
